import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import { Box, useTheme } from '@mui/material';
import { REGISTRATION_FIELD_FILL_BG } from '../registrationFormFieldStyles';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import updateLocale from 'dayjs/plugin/updateLocale';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
    weekStart: 1
});
const EDatePicker = ({ name, control, label, rules, error, bgColor = REGISTRATION_FIELD_FILL_BG, selectedTone = 'main', variant = false, disabled, tooltipText, ...rest }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const selectedBg = selectedTone === 'main'
        ? theme.palette.primary.main
        : (theme.palette.primary.darker ?? theme.palette.primary.darker);
    const popupPaperSx = {
        backgroundColor: variant ? '#2E3440' : 'white',
        color: variant ? theme.palette.common.white : theme.palette.text.primary,
        boxShadow: variant ? '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D' : 'none',
        /* Tabs (Date / Time) */
        '& .MuiTabs-root': {
            minHeight: 44
        },
        '& .MuiTab-root.Mui-selected': {
            color: selectedBg
        },
        '& .MuiTabs-indicator': {
            backgroundColor: selectedBg
        },
        /* Action bar buttons */
        '& .MuiPickersLayout-actionBar .MuiButton-root:first-of-type': {
            border: `1px solid ${selectedBg}`,
            color: selectedBg,
            backgroundColor: 'transparent'
        },
        '& .MuiPickersLayout-actionBar .MuiButton-root:last-of-type': {
            color: theme.palette.common.white,
            backgroundColor: selectedBg
        },
        '& .MuiPickersLayout-actionBar .MuiButton-root:first-of-type:hover': {
            backgroundColor: theme.palette.action.hover
        },
        /* Calendar days */
        '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: selectedBg,
            color: theme.palette.primary.contrastText
        },
        '& .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected)': {
            /* today (when not selected) */
            backgroundColor: 'transparent',
            color: theme.palette.text.primary
        },
        /* Clock colors */
        '& .MuiClockPointer-root': {
            backgroundColor: selectedBg
        },
        '& .MuiClockPointer-thumb': {
            borderColor: selectedBg,
            backgroundColor: selectedBg
        },
        '& .MuiClock-pin': {
            backgroundColor: selectedBg
        }
    };
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, tooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: tooltipText, size: 'small', variant: 'dark' }) }))] })), [label, tooltipText]);
    return (_jsxs(Box, { width: '100%', children: [_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => {
                    // Safely convert value to Day.js object with proper locale
                    const safeValue = field.value && (field.value instanceof Date || typeof field.value === 'string')
                        ? dayjs(field.value).locale('en-gb')
                        : null;
                    return (_jsx(DatePicker, { label: styledLabel, value: safeValue && safeValue.isValid && safeValue.isValid() ? safeValue : null, format: 'DD/MM/YYYY', open: open, disabled: disabled, onOpen: () => setOpen(true), onClose: () => setOpen(false), onChange: (date) => {
                            // Safely handle onChange
                            if (date && date.isValid()) {
                                field.onChange(date.toDate());
                            }
                            else {
                                field.onChange(null);
                            }
                        }, slotProps: {
                            textField: {
                                variant: 'filled',
                                fullWidth: true,
                                onClick: () => {
                                    if (!disabled)
                                        setOpen(true);
                                },
                                sx: {
                                    '.MuiInputBase-root.MuiFilledInput-root': {
                                        backgroundColor: `${bgColor} !important`,
                                        background: `${bgColor} !important`,
                                        borderRadius: '12px !important',
                                        overflow: 'hidden !important',
                                        pr: 2,
                                        cursor: !disabled ? 'pointer' : 'unset'
                                    },
                                    '.MuiIconButton-root': {
                                        border: 'none !important',
                                        color: '#454F5B !important',
                                        boxShadow: 'none !important'
                                    },
                                    '.MuiIconButton-root svg': {
                                        color: !disabled ? '#454F5B !important' : '' // explicitly setting dark color for icon when not disabled because originally icon has disabled color
                                    }
                                }
                            },
                            popper: {
                                sx: {
                                    '& .MuiPaper-root': popupPaperSx
                                }
                            },
                            /* Mobile dialog paper */
                            mobilePaper: {
                                sx: popupPaperSx
                            }
                        }, ...rest }));
                } }), _jsx(ErrorWithIcon, { error: error, mt: 0.5, ml: 0.5 })] }));
};
export default EDatePicker;
